import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { AppSidebar } from "@/components/app-sidebar";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, CreditCard, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import {
    SubscriptionPlan,
    TenantSubscription,
    getSubscriptionPlans,
    getTenantSubscription,
    initializePlans
} from "@/services/SubscriptionService";
import { createCheckoutSession } from "@/services/PaymentService";

export default function SubscriptionPage() {
    const { currentUser } = useAuth();
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [subscription, setSubscription] = useState<TenantSubscription | null>(null);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState<string | null>(null);

    useEffect(() => {
        const loadData = async () => {
            if (!currentUser?.tenantId) return;

            try {
                setLoading(true);
                // Ensure plans exist
                await initializePlans();

                const [fetchedPlans, fetchedSub] = await Promise.all([
                    getSubscriptionPlans(),
                    getTenantSubscription(currentUser.tenantId)
                ]);

                setPlans(fetchedPlans);
                setSubscription(fetchedSub);
            } catch (error) {
                console.error("Error loading subscription data:", error);
                toast({
                    variant: "destructive",
                    title: "Fehler",
                    description: "Abonnement-Daten konnten nicht geladen werden.",
                });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [currentUser]);

    const handleSubscribe = async (planId: string) => {
        if (!currentUser?.tenantId) return;

        try {
            setProcessingId(planId);
            const checkoutUrl = await createCheckoutSession(planId, currentUser.tenantId);

            // In a real app, we would redirect to Stripe
            // window.location.href = checkoutUrl;

            toast({
                title: "Weiterleitung zur Zahlung",
                description: "Sie werden nun zum Zahlungsanbieter weitergeleitet (Simulation).",
            });

            // Simulate success for now
            setTimeout(() => {
                toast({
                    title: "Erfolgreich",
                    description: "Abonnement wurde erfolgreich aktualisiert (Simulation).",
                });
                setProcessingId(null);
            }, 2000);

        } catch (error) {
            console.error("Error starting subscription:", error);
            toast({
                variant: "destructive",
                title: "Fehler",
                description: "Zahlungsvorgang konnte nicht gestartet werden.",
            });
            setProcessingId(null);
        }
    };

    const isCurrentPlan = (planId: string) => {
        return subscription?.planId === planId && subscription?.status === 'active';
    };

    if (loading) {
        return (
            <div className="flex h-screen overflow-hidden bg-background">
                <AppSidebar />
                <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen overflow-hidden bg-background">
            <AppSidebar />
            <div className="flex-1 overflow-y-auto p-8">
                <div className="max-w-5xl mx-auto">
                    <div className="mb-10 text-center">
                        <h1 className="text-3xl font-bold mb-4">Wählen Sie Ihren Plan</h1>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            Wachsen Sie mit LeadsPro. Jederzeit kündbar. Keine versteckten Kosten.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {plans.map((plan) => (
                            <Card key={plan.id} className={`flex flex-col relative ${isCurrentPlan(plan.id) ? 'border-primary border-2 shadow-lg' : ''}`}>
                                {isCurrentPlan(plan.id) && (
                                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                        <Badge className="bg-primary text-primary-foreground px-3 py-1">Aktueller Plan</Badge>
                                    </div>
                                )}
                                <CardHeader>
                                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                                    <CardDescription>
                                        <span className="text-3xl font-bold text-foreground">€{plan.price}</span>
                                        <span className="text-muted-foreground"> / Monat</span>
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="flex-1">
                                    <ul className="space-y-3">
                                        {plan.features.map((feature, index) => (
                                            <li key={index} className="flex items-start">
                                                <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                                                <span className="text-sm">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                                <CardFooter>
                                    <Button
                                        className="w-full"
                                        variant={isCurrentPlan(plan.id) ? "outline" : "default"}
                                        disabled={isCurrentPlan(plan.id) || !!processingId}
                                        onClick={() => handleSubscribe(plan.id)}
                                    >
                                        {processingId === plan.id ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : isCurrentPlan(plan.id) ? (
                                            "Aktueller Plan"
                                        ) : (
                                            <>
                                                <CreditCard className="h-4 w-4 mr-2" />
                                                {subscription ? "Wechseln" : "Abonnieren"}
                                            </>
                                        )}
                                    </Button>
                                </CardFooter>
                            </Card>
                        ))}
                    </div>

                    {subscription && (
                        <div className="mt-12">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Abonnement verwalten</CardTitle>
                                    <CardDescription>Details zu Ihrem aktuellen Abonnement</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <p className="text-sm font-medium text-muted-foreground mb-1">Status</p>
                                            <Badge variant={subscription.status === 'active' ? 'default' : 'destructive'}>
                                                {subscription.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                                            </Badge>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-muted-foreground mb-1">Nächste Abrechnung</p>
                                            <p className="font-medium">
                                                {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : '-'}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                                <CardFooter>
                                    <Button variant="destructive" size="sm">Abonnement kündigen</Button>
                                </CardFooter>
                            </Card>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
